import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { CrudApi } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  EmployeeValeDto,
  EmployeeValeInput,
  EmployeeValeListItem,
  EmployeeValeParcelaDto,
} from './types';

/**
 * CRUD do header de Vale aninhado em um Colaborador. `employeeId` fecha no
 * escopo. Mesma estrutura de `createEmployeeFaltasApi`/`createEmployeeFeriasApi`.
 *
 * Id é numérico autonum (`valeCodigo`), tratado como number aqui.
 */
export function createEmployeeValesApi(
  employeeId: number,
): CrudApi<EmployeeValeListItem, EmployeeValeDto, EmployeeValeInput, number> {
  const BASE = `/v1/employees/${employeeId}/vales`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<EmployeeValeListItem>> {
      const { data } = await api.get<{
        data: EmployeeValeListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params: toGenexusParams(query) });
      return normalizeListEnvelope(data);
    },

    async get(id: number): Promise<EmployeeValeDto> {
      const { data } = await api.get<{ data: EmployeeValeDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async create(input: EmployeeValeInput): Promise<EmployeeValeDto> {
      const { data } = await api.post<{ data: EmployeeValeDto }>(BASE, input);
      return data.data;
    },

    async update(id: number, input: EmployeeValeInput): Promise<EmployeeValeDto> {
      const { data } = await api.put<{ data: EmployeeValeDto }>(`${BASE}/${id}`, input);
      return data.data;
    },

    async remove(id: number): Promise<void> {
      await api.delete(`${BASE}/${id}`);
    },
  };
}

/** Lista parcelas de um vale (grandchild read-only). */
export async function listParcelasDoVale(
  employeeId: number,
  valeCodigo: number,
  query?: DataTableQuery,
): Promise<ListEnvelope<EmployeeValeParcelaDto>> {
  const { data } = await api.get<{
    data: EmployeeValeParcelaDto[];
    meta: { total: number; pagina?: number; porPagina?: number };
  }>(`/v1/employees/${employeeId}/vales/${valeCodigo}/parcelas`, {
    params: query ? toGenexusParams(query) : undefined,
  });
  return normalizeListEnvelope(data);
}

export async function getParcelaDoVale(
  employeeId: number,
  valeCodigo: number,
  parcelaCodigo: number,
): Promise<EmployeeValeParcelaDto> {
  const { data } = await api.get<{ data: EmployeeValeParcelaDto }>(
    `/v1/employees/${employeeId}/vales/${valeCodigo}/parcelas/${parcelaCodigo}`,
  );
  return data.data;
}

/** Input de baixa de parcela (Fase 2 Pacote A — card [h7TE7xcf]). */
export interface BaixarParcelaInput {
  dataPagamento: string; // YYYY-MM-DD
  valorPago: number;
  nfColaboradorCodigo?: number | null;
  nfNotaFiscalCodigo?: number | null;
}

/**
 * Registra pagamento da parcela. Quando todas as parcelas do vale ficam pagas,
 * o header do vale transita server-side para `status='PAG'` — o caller deve
 * invalidar a query do vale após a mutation.
 */
export async function baixarParcelaDoVale(
  employeeId: number,
  valeCodigo: number,
  parcelaCodigo: number,
  input: BaixarParcelaInput,
): Promise<EmployeeValeParcelaDto> {
  const { data } = await api.post<{ data: EmployeeValeParcelaDto }>(
    `/v1/employees/${employeeId}/vales/${valeCodigo}/parcelas/${parcelaCodigo}/baixar`,
    input,
  );
  return data.data;
}
